import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { formatDateTime, meetingTypeLabel, getDetailPath, toDate } from '../../utils/meetingUtils';

import Spinner from '../../components/Spinner/Spinner'
import { toast } from 'react-hot-toast';
import MeetingSearch from './MeetingSearch'
import { getMeetingsPage } from '../../features/meeting/meetingSlice'

function MeetingPastList() {
    const PAGE_SIZE = 15;
    const [filteredMeetings, setFilteredMeetings] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const isVisibleMeeting = (m) => m?.state !== false && m?.state !== 'false';

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth);
    const isAuthenticated = useSelector((state) => state.auth.user !== null);
    const { meetings, meetingsPagination, loadingStates, errors, message } = useSelector((state) => state.meeting);
    const isLoading = loadingStates?.getMeetingsPage;
    const loadError = errors?.getMeetingsPage;
    const hasMore = !!meetingsPagination?.hasMore;
    const nextCursor = meetingsPagination?.nextCursor ?? null;

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login')
        }
    }, [isAuthenticated, navigate])

    useEffect(() => {
        if (!isAuthenticated) return;
        dispatch(getMeetingsPage({ pageSize: PAGE_SIZE, cursor: null, searchTerm, append: false }))
    }, [isAuthenticated, dispatch, location.pathname])

    useEffect(() => {
        if (loadError) {
            toast.error(loadError || message);
        }
    }, [loadError, message])

    useEffect(() => {
        const now = new Date();
        const past = meetings.filter(m => {
            if (!isVisibleMeeting(m)) return false;
            const end = m.endDate?.toDate ? m.endDate.toDate() : new Date(m.endDate);
            return end < now;
        });
        setFilteredMeetings(past);
    }, [meetings]);

    const handleFilteredMeetings = useCallback((filtered) => {
        setFilteredMeetings((filtered || []).filter(isVisibleMeeting));
    }, []);

    if (isLoading) {
        return <Spinner />
    }

    const formatDate = (date) => formatDateTime(date);
    const formatHourMinute = (value) => {
        const d = toDate(value);
        if (!d) return "-";
        return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    };
    const getMeetingDurationText = (startDate, endDate) => {
        const start = toDate(startDate);
        const end = toDate(endDate);
        if (!start || !end) return "-";
        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) return "-";
        const totalMin = Math.round(diffMs / 60000);
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (h > 0 && m > 0) return `${h} sa ${m} dk`;
        if (h > 0) return `${h} sa`;
        return `${m} dk`;
    };
    const cardStyle = {
        background: "#fff",
        borderRadius: "14px",
        padding: "16px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
        borderLeft: "4px solid #5f60b9",
        textDecoration: "none",
        display: "block",
        transition: "box-shadow 0.2s",
    };
    const InfoRow = ({ icon, text }) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
            <i className={`fa fa-${icon}`} style={{ color: "#5f60b9", fontSize: "13px", width: "14px", textAlign: "center" }} />
            <span style={{ color: "#555", fontSize: "0.82rem" }}>{text}</span>
        </div>
    );
    const MeetingCard = ({ meeting }) => {
        const detailPath = getDetailPath(meeting);
        const content = (
            <>
                <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", marginBottom: "8px" }}>
                    {meeting?.title || "Başlıksız Toplantı"}
                </div>
                {String(meeting?.status ?? '0') === '1' && (
                    <div style={{ marginBottom: "8px" }}>
                        <span className="badge light badge-danger">Toplantı İptal Edildi</span>
                    </div>
                )}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        columnGap: "16px",
                    }}
                >
                    <InfoRow icon="calendar" text={formatDate(meeting.startDate)} />
                    <InfoRow icon="clock-o" text={`Saat: ${formatHourMinute(meeting.startDate)} - ${formatHourMinute(meeting.endDate)}`} />
                    <InfoRow icon="hourglass-half" text={`Süre: ${getMeetingDurationText(meeting.startDate, meeting.endDate)}`} />
                    <InfoRow icon="briefcase" text={meeting.isactivity ? "Etkinlik" : "Toplantı"} />
                    <InfoRow icon="star" text={meetingTypeLabel(meeting.type)} />
                    <InfoRow icon="user" text={`${meeting.users?.length ?? 0} Katılımcı`} />
                </div>
            </>
        );
        if (!detailPath) return <div style={cardStyle}>{content}</div>;
        return <Link to={detailPath} style={cardStyle}>{content}</Link>;
    };
    const handleLoadMore = () => {
        if (!hasMore || isLoading) return;
        dispatch(getMeetingsPage({
            pageSize: PAGE_SIZE,
            cursor: nextCursor,
            searchTerm,
            append: true,
        }));
    };

    const handleRefresh = () => {
        dispatch(getMeetingsPage({ pageSize: PAGE_SIZE, cursor: null, searchTerm, append: false }));
    };

    const handleSearchSubmit = (value) => {
        const nextSearch = (value || '').trim();
        setSearchTerm(nextSearch);
        dispatch(getMeetingsPage({ pageSize: PAGE_SIZE, cursor: null, searchTerm: nextSearch, append: false }));
    };

    return (
        <>
            {/* Search Component */}
            <MeetingSearch
                onFilteredMeetings={handleFilteredMeetings}
                onRefresh={handleRefresh}
                onSearch={handleSearchSubmit}
            />
            
            <div className="row">

                {filteredMeetings.length > 0 ? (
                    <>
                        {filteredMeetings.map((meeting, index) => (
                            <div className="col-12 mb-3" key={index}>
                                <MeetingCard meeting={meeting} />
                            </div>
                        ))}
                    </>
                ) : (
                    <h3>Toplantı Bulunamadı</h3>
                )}
            </div >

            {hasMore && (
                <div className="d-flex justify-content-center mt-4">
                    <button className="btn btn-primary" onClick={handleLoadMore}>
                        {isLoading ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
                    </button>
                </div>
            )}
        </>

    )
}

export default MeetingPastList
